import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactCrop, { centerCrop, makeAspectCrop, type PercentCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import type { DemoImageCrop } from '../../../common/hooks/use-demo-sites-api.hook';

export type PhotoCrop = Omit<DemoImageCrop, 'url'>;

const WHOLE: PercentCrop = { unit: '%', x: 0, y: 0, width: 100, height: 100 };

/** A crop that keeps (nearly) the whole photo is no crop at all. */
export const isWholePhoto = (c: PhotoCrop | null | undefined): boolean =>
  !c || (c.width >= 99.5 && c.height >= 99.5);

/**
 * The cropped part of a photo, fitted in its tile. The photo is a background
 * scaled so the crop fills the box; the box takes the crop's own shape, which
 * needs the photo's size in pixels.
 */
export const CroppedPreview: React.FC<{ src: string; crop: PhotoCrop; natural?: { w: number; h: number } }> = ({ src, crop, natural }) => {
  const ratio = natural ? (crop.width * natural.w) / (crop.height * natural.h) : 1;
  const pos = (start: number, size: number) => (size >= 100 ? 0 : (start / (100 - size)) * 100);
  return (
    <div className="flex size-full items-center justify-center">
      <div
        className="max-h-full max-w-full"
        style={{
          aspectRatio: String(ratio),
          width: ratio >= 1 ? '100%' : 'auto',
          height: ratio >= 1 ? 'auto' : '100%',
          backgroundImage: `url("${src}")`,
          backgroundRepeat: 'no-repeat',
          backgroundSize: `${(100 / crop.width) * 100}% ${(100 / crop.height) * 100}%`,
          backgroundPosition: `${pos(crop.x, crop.width)}% ${pos(crop.y, crop.height)}%`,
        }}
      />
    </div>
  );
};

interface Props {
  src: string | null;
  initial?: PhotoCrop;
  onClose: () => void;
  /** null: the photo is used whole. */
  onApply: (crop: PhotoCrop | null) => void;
}

/**
 * Crop one product photo before the demo is generated — e.g. one tile out of a
 * shot of several, or the tile without the background around it. Only the
 * area is chosen here; the API cuts it from the photo it downloads.
 */
export const PhotoCropDialog: React.FC<Props> = ({ src, initial, onClose, onApply }) => {
  const { t } = useTranslation();
  const [crop, setCrop] = useState<PercentCrop>(WHOLE);
  const [shape, setShape] = useState<'free' | 'square'>('free');
  const [media, setMedia] = useState<{ w: number; h: number } | null>(null);

  useEffect(() => {
    if (!src) return;
    setCrop(initial ? { unit: '%', ...initial } : WHOLE);
    setShape('free');
    setMedia(null);
  }, [src, initial]);

  const square = (w: number, h: number) => centerCrop(makeAspectCrop({ unit: '%', width: 90 }, 1, w, h), w, h);

  const changeShape = (value: string) => {
    if (value !== 'free' && value !== 'square') return;
    setShape(value);
    if (value === 'square' && media) setCrop(square(media.w, media.h));
  };

  return (
    <Dialog open={!!src} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('SUPER_ADMIN.DEMO_GENERATOR.CROP_TITLE')}</DialogTitle>
          <DialogDescription>{t('SUPER_ADMIN.DEMO_GENERATOR.CROP_HINT')}</DialogDescription>
        </DialogHeader>

        <ToggleGroup type="single" variant="outline" size="sm" value={shape} onValueChange={changeShape} className="self-start">
          <ToggleGroupItem value="free">{t('SUPER_ADMIN.DEMO_GENERATOR.CROP_FREE')}</ToggleGroupItem>
          <ToggleGroupItem value="square">{t('SUPER_ADMIN.DEMO_GENERATOR.CROP_SQUARE')}</ToggleGroupItem>
        </ToggleGroup>

        {src && (
          <div className="flex max-h-[60vh] justify-center overflow-auto rounded-lg bg-muted">
            <ReactCrop
              crop={crop}
              onChange={(_, percent) => setCrop(percent)}
              aspect={shape === 'square' ? 1 : undefined}
              keepSelection
              ruleOfThirds
            >
              <img
                src={src}
                alt=""
                referrerPolicy="no-referrer"
                className="max-h-[60vh] w-auto"
                onLoad={(e) => {
                  const { width, height } = e.currentTarget;
                  setMedia({ w: width, h: height });
                }}
              />
            </ReactCrop>
          </div>
        )}

        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="ghost" onClick={() => onApply(null)}>
            {t('SUPER_ADMIN.DEMO_GENERATOR.CROP_RESET')}
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>{t('SUPER_ADMIN.DEMO_GENERATOR.CROP_CANCEL')}</Button>
            <Button
              onClick={() => {
                const { x, y, width, height } = crop;
                onApply(isWholePhoto(crop) || !width || !height ? null : { x, y, width, height });
              }}
            >
              {t('SUPER_ADMIN.DEMO_GENERATOR.CROP_APPLY')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
